import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Compass, Sparkles, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { IMG } from '../lib/images';
import { useTheme } from '../contexts/ThemeContext';

const BENEFITS = [
  'Migration risk map',
  '90-day rollout blueprint',
  'Hidden cost audit',
  'Vendor lock-in review',
];

const StackConsultingPrime = ({ compact = false }) => {
  const { theme } = useTheme();
  const isLight = theme === 'light';

  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className={`relative overflow-hidden rounded-2xl ${compact ? '' : ''}`}
      style={{
        background: isLight ? 'rgba(255,255,255,0.96)' : 'rgba(8,8,18,0.92)',
        border: isLight ? '1px solid rgba(15,23,42,0.12)' : '1px solid rgba(255,255,255,0.12)',
        boxShadow: isLight ? '0 18px 46px rgba(15,23,42,0.08)' : '0 20px 60px rgba(0,0,0,0.5), 0 0 50px rgba(255,232,0,0.10)',
      }}
      data-testid="stack-consulting-prime"
    >
      {/* Full-bleed background image with heavy overlay */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: isLight
            ? `linear-gradient(100deg, rgba(255,255,255,0.95) 20%, rgba(246,248,251,0.88) 100%), url(${IMG.hero.consulting})`
            : `linear-gradient(100deg, rgba(8,8,18,0.97) 48%, rgba(8,8,18,0.70) 100%), url(${IMG.hero.consulting})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center right',
        }}
      />
      <div className="absolute inset-0 prime-aurora-bg" />

      <div className={`relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-6 items-center ${compact ? 'p-5' : 'p-8'}`}>
        <div className="lg:col-span-8">
          <p className="text-xs uppercase tracking-[0.2em] font-mono text-cyber-yellow mb-3 inline-flex items-center gap-2">
            <Compass size={14} /> Decision safety net
          </p>
          <h3
            className={`font-black mb-3 ${compact ? 'text-lg' : 'text-2xl'} ${isLight ? 'text-[#0F172A]' : 'text-white'}`}
            style={{ fontFamily: 'Unbounded, sans-serif' }}
          >
            Want an expert to pressure-test this stack before you commit?
          </h3>
          <p className="text-sm text-text-secondary mb-5 max-w-xl">
            We review workflow fit, migration risk, hidden implementation costs, and vendor lock-in concerns in one focused session.
          </p>
          <div className="grid grid-cols-2 gap-y-2 gap-x-4 mb-5">
            {BENEFITS.map((b) => (
              <p key={b} className="inline-flex items-center gap-2 text-sm text-text-secondary">
                <CheckCircle2 size={14} className="text-laser-green flex-shrink-0" /> {b}
              </p>
            ))}
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/consulting#start"
              className="btn-primary-enhanced inline-flex items-center gap-2"
              style={{ boxShadow: '0 0 22px rgba(255,232,0,0.28)' }}
            >
              <Sparkles size={15} /> Book consulting
              <ArrowRight size={15} />
            </Link>
            <Link to="/transparency" className="btn-secondary">See commission disclosures first</Link>
          </div>
        </div>

        <div className="hidden lg:block lg:col-span-4">
          <div className="relative h-[200px]">
            <img
              src={IMG.card.meetingRoom}
              alt="Strategy session"
              className={`absolute inset-0 w-full h-full object-cover rounded-xl ${isLight ? 'border border-[#D8E0EA] shadow-[0_16px_44px_rgba(15,23,42,0.12)]' : 'border border-white/15 shadow-[0_16px_44px_rgba(0,0,0,0.5)]'}`}
            />
            <div className={`absolute bottom-3 left-3 right-3 px-3 py-2 backdrop-blur-sm rounded-lg ${isLight ? 'bg-white/90 border border-[#D8E0EA]' : 'bg-black/75 border border-cyber-yellow/30'}`}>
              <p className="text-[10px] uppercase tracking-[0.14em] font-mono text-cyber-yellow">Expert session</p>
              <p className={`text-xs mt-0.5 ${isLight ? 'text-[#0F172A]' : 'text-white'}`}>Avg. teams save 3+ weeks of trial & error</p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default StackConsultingPrime;

